/**
 * STICKER BUILDER PRO V6.1 - AVISOCAR (STICKER REAL)
 * ============================================================
 * MASTER FIX V6.1:
 * - DRAG + RESIZE for ALL elements using react-rnd
 * - Icons selection up to 3, toggle off/on, remove button
 * - Single emoji with remove capability
 * - Larger preview (420-520px) with zoom slider
 * - QR badge renamed to "QR (Sistema)"
 * - SILENT Undo/Redo (NO toast spam)
 * - Export PNG/PDF uses CURRENT state (no cache issues)
 * - Footer safe area (10-14% reserved)
 * - "Escanéame si bloqueo" COMPLETELY REMOVED
 * - Membership expiration (3 months) fully supported
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rnd } from "react-rnd";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { 
  X, ArrowLeft, Download, FileText, Image, Sparkles, 
  Loader2, Check, Palette, RefreshCw, Wand2, Type,
  Smile, Zap, Eye, ChevronRight, Layers, Settings,
  Undo2, Redo2, AlignCenter, AlignLeft, AlignRight,
  Edit3, Grid3X3, LayoutTemplate, Save, Bot, Cpu, Move,
  Trash2, ZoomIn, ZoomOut, Maximize2
} from "lucide-react";
import {
  getPalette,
  PALETTES
} from "../../data/stickerTemplates";
import useStickerCatalog from "../../hooks/useStickerCatalog";
import { localizeTemplate } from "../../data/stickerTemplatesI18n";
import { localizeSavedTexts } from "../../data/stickerSavedI18n";
import { 
  STICKER_CHARACTERS,
  CHARACTER_CATEGORIES,
  getCharactersByCategory,
  ANIMATION_CONFIGS 
} from "../../data/stickerCharacters";
import {
  GRADIENT_PRESETS,
  TEXT_COLORS,
  ICON_PACKS,
  STICKER_STYLES,
  DEFAULT_STYLE_STATE,
  STICKER_FOOTER,
  generateGradientCSS,
  StyleHistory,
} from "../../lib/sticker_styles_engine_v5";
import { generateSticker, fetchLatestQR } from "../../lib/sticker_generator_v5";
import { getStickerFooter } from "../../lib/sticker_styles_engine_v5";
import { useI18n } from "../../i18n";
// V5.1: AI Engine Import
import { 
  generateAIVariants, 
  AI_TONES, 
  AI_URGENCY_LEVELS 
} from "../../services/sticker_ai_engine";

const API = '';

// ============================================
// V5.2: TEXT SIZE CONTROL MAPS
// ============================================
const TEXT_SIZE_MAP = {
  title: { S: "14px", M: "18px", L: "22px", XL: "28px" },
  subtitle: { S: "10px", M: "12px", L: "14px" },
  slogan: { S: "11px", M: "13px", L: "16px" },
};

// ============================================
// V5 FIX: Premium Icon Packs by Category
// ============================================
const PREMIUM_ICONS = {
  vehiculos: [
    { id: "car", icon: "🚗", name: "Auto" },
    { id: "tow", icon: "🚛", name: "Grúa" },
    { id: "parking", icon: "🅿️", name: "Estacionar" },
    { id: "lights", icon: "🚨", name: "Luces" },
    { id: "alarm", icon: "🔔", name: "Alarma" },
  ],
  ninos: [
    { id: "child", icon: "🧒", name: "Niño" },
    { id: "family", icon: "👨‍👩‍👧", name: "Familia" },
    { id: "school", icon: "🎒", name: "Escuela" },
    { id: "park", icon: "🎠", name: "Parque" },
    { id: "bracelet", icon: "📿", name: "Pulsera" },
  ],
  mascotas: [
    { id: "paw", icon: "🐾", name: "Huella" },
    { id: "collar", icon: "🦮", name: "Collar" },
    { id: "pet_home", icon: "🏠", name: "Casa" },
    { id: "vet", icon: "💉", name: "Veterinario" },
    { id: "bone", icon: "🦴", name: "Hueso" },
  ],
  objetos: [
    { id: "luggage", icon: "🧳", name: "Maleta" },
    { id: "airport", icon: "✈️", name: "Aeropuerto" },
    { id: "backpack", icon: "🎒", name: "Mochila" },
    { id: "keys", icon: "🔑", name: "Llaves" },
    { id: "wallet", icon: "👛", name: "Billetera" },
  ],
  viajes: [
    { id: "plane", icon: "✈️", name: "Avión" },
    { id: "passport", icon: "🛂", name: "Pasaporte" },
    { id: "map", icon: "🗺️", name: "Mapa" },
    { id: "hotel", icon: "🏨", name: "Hotel" },
    { id: "ticket", icon: "🎫", name: "Ticket" },
  ],
  emergencias: [
    { id: "sos", icon: "🆘", name: "SOS" },
    { id: "ambulance", icon: "🚑", name: "Ambulancia" },
    { id: "crash", icon: "💥", name: "Choque" },
    { id: "police", icon: "🚔", name: "Policía" },
    { id: "fire", icon: "🔥", name: "Fuego" },
  ],
  privacidad: [
    { id: "lock", icon: "🔒", name: "Candado" },
    { id: "shield", icon: "🛡️", name: "Escudo" },
    { id: "anonymous", icon: "🎭", name: "Anónimo" },
    { id: "eye_off", icon: "🙈", name: "Oculto" },
    { id: "secure", icon: "🔐", name: "Seguro" },
  ],
};

// Get icons for a category
const getIconsForCategory = (categoryId) => {
  return PREMIUM_ICONS[categoryId] || PREMIUM_ICONS.vehiculos;
};

// ============================================
// CONFETTI EFFECT
// ============================================
const Confetti = ({ show }) => {
  if (!show) return null;
  const colors = ["#10B981", "#3B82F6", "#F59E0B", "#EC4899", "#8B5CF6", "#06B6D4"];
  
  return (
    <div className="fixed inset-0 pointer-events-none z-[9999]">
      {[...Array(40)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ y: -20, opacity: 1, rotate: 0 }}
          animate={{ y: "100vh", opacity: 0, rotate: 720 }}
          transition={{ duration: 2 + Math.random(), delay: Math.random() * 0.3 }}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            backgroundColor: colors[Math.floor(Math.random() * colors.length)],
            width: `${6 + Math.random() * 6}px`,
            height: `${6 + Math.random() * 6}px`,
            borderRadius: Math.random() > 0.5 ? "50%" : "2px",
          }}
        />
      ))}
    </div>
  );
};

// ============================================
// ANIMATED CHARACTER COMPONENT
// ============================================
const AnimatedCharacter = ({ emoji, animation = "float", size = 40 }) => {
  const configs = {
    float: { animate: { y: [0, -6, 0] }, transition: { duration: 2, repeat: Infinity, ease: "easeInOut" } },
    bounce: { animate: { y: [0, -10, 0] }, transition: { duration: 0.6, repeat: Infinity } },
    pulse: { animate: { scale: [1, 1.1, 1] }, transition: { duration: 1.5, repeat: Infinity } },
    wiggle: { animate: { rotate: [-5, 5, -5] }, transition: { duration: 0.5, repeat: Infinity } },
  };
  const config = configs[animation] || configs.float;
  
  return (
    <motion.span
      animate={config.animate}
      transition={config.transition}
      style={{ fontSize: size, display: "inline-block" }}
    >
      {emoji}
    </motion.span>
  );
};

// ============================================
// V5 FIX: TEMPLATE CARD WITH PROPER ALIGNMENT
// ============================================
const TemplateCard = ({ template, isSelected, onClick }) => {
  const palette = getPalette(template.palette) || { primary: "#10B981", secondary: "#6EE7B7" };
  
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        relative flex flex-col h-full min-h-[140px] p-3 rounded-xl border-2 transition-all text-left
        ${isSelected 
          ? "border-emerald-500 ring-2 ring-emerald-500/30 shadow-lg shadow-emerald-500/10" 
          : "border-slate-200 hover:border-slate-300 hover:shadow-md bg-white"
        }
      `}
      style={{ 
        background: isSelected 
          ? `linear-gradient(135deg, ${palette.primary}10 0%, ${palette.secondary}10 100%)`
          : "white",
      }}
      data-testid={`template-${template.id}`}
    >
      {/* Selection checkmark - FIXED POSITION */}
      {isSelected && (
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-md z-10"
        >
          <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
        </motion.div>
      )}
      
      {/* Featured badge */}
      {template.featured && (
        <div className="absolute top-1.5 left-1.5">
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
        </div>
      )}

      {/* Content with flex-grow for alignment */}
      <div className="flex flex-col flex-grow gap-2">
        {/* Emoji */}
        {template.emoji && (
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center text-2xl shrink-0"
            style={{ backgroundColor: palette.primary + "20" }}
          >
            {template.emoji}
          </div>
        )}
        
        {/* Text content */}
        <div className="flex-grow">
          <p className="text-xs font-semibold text-slate-800 line-clamp-2 leading-tight mb-1">
            {template.title}
          </p>
          {template.slogan && (
            <p className="text-[10px] text-slate-500 line-clamp-2">
              {template.slogan}
            </p>
          )}
        </div>
      </div>
      
      {/* Bottom bar - ALWAYS AT BOTTOM */}
      <div 
        className="mt-auto pt-2 h-1.5 rounded-full shrink-0"
        style={{ background: `linear-gradient(90deg, ${palette.primary}, ${palette.secondary})` }}
      />
    </motion.button>
  );
};

// ============================================
// V5 FIX: CATEGORY SELECTOR (Horizontal Scroll)
// ============================================
const CategorySelector = ({ selectedCategory, onSelect, categories = [] }) => {
  const { t: catT } = useI18n();
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-300">
      {categories.map((cat) => (
        <motion.button
          key={cat.id}
          onClick={() => onSelect(cat.id)}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className={`
            flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap
            transition-all border
            ${selectedCategory === cat.id
              ? "bg-emerald-500 text-white border-emerald-500 shadow-sm"
              : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
            }
          `}
          data-testid={`category-${cat.id}`}
        >
          <span className="text-base">{cat.icon}</span>
          <span>{catT(`sticker.cat.${cat.id}`) !== `sticker.cat.${cat.id}` ? catT(`sticker.cat.${cat.id}`) : cat.name}</span>
        </motion.button>
      ))}
    </div>
  );
};

// ============================================
// V6.1: LIVE PREVIEW - DRAG + RESIZE with react-rnd
// V6.4: NORMALIZED POSITIONS (percentages) for WYSIWYG export
// ============================================
const PREVIEW_BASE_SIZE = 420; // Reference size for the preview canvas

const LivePreview = ({ 
  template, 
  styleOptions, 
  qrInfo, 
  isLoadingQr,
  onRefreshQr,
  onDragStop, // V6.0: callback to save drag positions (NOW with normalized coords)
  onResizeStop, // V6.1: callback to save element sizes (NOW with normalized coords)
  zoomLevel = 100 // V6.1: zoom level (50-150%)
}) => {
  const { t: sqT, lang: sqLang } = useI18n();
  const palette = getPalette(template?.palette) || { primary: "#10B981", secondary: "#6EE7B7", accent: "#FFFFFF" };
  
  // V6.4: Calculate actual preview size based on zoom
  const actualPreviewSize = Math.round(PREVIEW_BASE_SIZE * (zoomLevel / 100));
  
  // V6.4: Element positions and sizes (in pixels for display)
  // Default positions as percentages: icons top-left, emoji bottom-right
  const [iconState, setIconState] = useState({ x: 10, y: 10, width: 80, height: 32 });
  const [emojiState, setEmojiState] = useState({ x: 300, y: 300, width: 60, height: 60 });
  const [qrState, setQrState] = useState({ x: 0, y: 0, width: 100, height: 100 }); // Centered in container
  
  const gradientCSS = styleOptions.backgroundGradient 
    ? generateGradientCSS(styleOptions.backgroundGradient)
    : `linear-gradient(135deg, ${palette.primary} 0%, ${palette.secondary} 100%)`;
  
  const textColor = styleOptions.primaryTextColor?.value || palette.accent || "#FFFFFF";
  const secondaryColor = styleOptions.secondaryTextColor?.value || "#FFFFFFAA";
  
  const title = styleOptions.title || template?.title || "";
  const subtitle = styleOptions.subtitle || template?.subline || "";
  const slogan = styleOptions.slogan || template?.slogan || "";
  
  // Active icons
  const activeIconEmojis = (styleOptions.activeIcons || []).slice(0, 3).map(iconId => {
    for (const cat of Object.values(PREMIUM_ICONS)) {
      const found = cat.find(i => i.id === iconId);
      if (found) return found.icon;
    }
    return "⭐";
  });
  
  // V6.1: QR URL with proper cache buster
  const qrImageUrl = qrInfo?.qr_url 
    ? `${qrInfo.qr_url}${qrInfo.qr_url.includes('?') ? '&' : '?'}t=${qrInfo.qr_payload_version || Date.now()}`
    : null;
  
  // V6.4: Load saved positions (stored as percentages) and convert to pixels
  useEffect(() => {
    if (styleOptions.layoutOffsets?.icons) {
      const saved = styleOptions.layoutOffsets.icons;
      // V6.4: Convert from percentages to pixels if stored as percentages
      const x = saved.xPercent !== undefined ? saved.xPercent * actualPreviewSize : saved.x;
      const y = saved.yPercent !== undefined ? saved.yPercent * actualPreviewSize : saved.y;
      const width = saved.widthPercent !== undefined ? saved.widthPercent * actualPreviewSize : (saved.width || 80);
      const height = saved.heightPercent !== undefined ? saved.heightPercent * actualPreviewSize : (saved.height || 32);
      setIconState({ x, y, width, height });
    }
    if (styleOptions.layoutOffsets?.emoji) {
      const saved = styleOptions.layoutOffsets.emoji;
      // V6.4: Convert from percentages to pixels
      const x = saved.xPercent !== undefined ? saved.xPercent * actualPreviewSize : saved.x;
      const y = saved.yPercent !== undefined ? saved.yPercent * actualPreviewSize : saved.y;
      const width = saved.widthPercent !== undefined ? saved.widthPercent * actualPreviewSize : (saved.width || 60);
      const height = saved.heightPercent !== undefined ? saved.heightPercent * actualPreviewSize : (saved.height || 60);
      setEmojiState({ x, y, width, height });
    }
    if (styleOptions.layoutOffsets?.qr) {
      setQrState(prev => ({ ...prev, ...styleOptions.layoutOffsets.qr }));
    }
  }, [styleOptions.layoutOffsets, actualPreviewSize]);
  
  // V6.4: Helper to normalize position to percentages
  const normalizePosition = (pixelPos, previewSize) => ({
    x: pixelPos.x,
    y: pixelPos.y,
    width: pixelPos.width,
    height: pixelPos.height,
    // V6.4: Store BOTH pixel and percentage values for maximum compatibility
    xPercent: pixelPos.x / previewSize,
    yPercent: pixelPos.y / previewSize,
    widthPercent: pixelPos.width / previewSize,
    heightPercent: pixelPos.height / previewSize
  });
  
  // V6.4: Handle icon drag/resize - SAVE NORMALIZED POSITIONS
  const handleIconDragStop = (e, d) => {
    const newState = { ...iconState, x: d.x, y: d.y };
    setIconState(newState);
    // V6.4: Send normalized position to parent
    if (onDragStop) onDragStop('icons', normalizePosition(newState, actualPreviewSize));
  };
  
  const handleIconResizeStop = (e, direction, ref, delta, position) => {
    const newState = { 
      x: position.x, 
      y: position.y, 
      width: ref.offsetWidth, 
      height: ref.offsetHeight 
    };
    setIconState(newState);
    // V6.4: Send normalized position to parent
    if (onResizeStop) onResizeStop('icons', normalizePosition(newState, actualPreviewSize));
  };
  
  // V6.4: Handle emoji drag/resize - SAVE NORMALIZED POSITIONS
  const handleEmojiDragStop = (e, d) => {
    const newState = { ...emojiState, x: d.x, y: d.y };
    setEmojiState(newState);
    // V6.4: Send normalized position to parent
    if (onDragStop) onDragStop('emoji', normalizePosition(newState, actualPreviewSize));
  };
  
  const handleEmojiResizeStop = (e, direction, ref, delta, position) => {
    const newState = { 
      x: position.x, 
      y: position.y, 
      width: ref.offsetWidth, 
      height: ref.offsetHeight 
    };
    setEmojiState(newState);
    // V6.4: Send normalized position to parent
    if (onResizeStop) onResizeStop('emoji', normalizePosition(newState, actualPreviewSize));
  };
  
  // V6.4: Use actualPreviewSize for rendering (already calculated above)
  const previewSize = actualPreviewSize;
  
  return (
    <div className="flex flex-col items-center">
      {/* V6.1: QR Version info - Renamed badge */}
      <div className="flex items-center gap-2 mb-3">
        <Badge 
          className="bg-emerald-500/20 text-emerald-700 text-[10px] border-emerald-200"
          title="Versión interna del sistema QR - no afecta tu sticker"
        >
          {sqT("sticker_extra.qr_system")} v{qrInfo?.qr_payload_version || 1}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRefreshQr}
          disabled={isLoadingQr}
          className="h-6 text-xs text-slate-500 hover:text-emerald-600"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${isLoadingQr ? "animate-spin" : ""}`} />
          {sqT("sticker.sync")}
        </Button>
      </div>
      
      {/* V6.1: Sticker Preview Container - LARGER */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative rounded-2xl overflow-hidden"
        style={{ 
          width: previewSize,
          height: previewSize,
          background: gradientCSS,
          boxShadow: styleOptions.glowEnabled 
            ? `0 0 30px ${palette.primary}40, 0 15px 50px rgba(0,0,0,0.25)` 
            : "0 15px 50px rgba(0,0,0,0.25)"
        }}
        data-testid="sticker-preview"
      >
        {/* Frame border */}
        {styleOptions.frameEnabled !== false && (
          <div className="absolute inset-2 border-2 border-white/25 rounded-xl pointer-events-none" />
        )}
        
        {/* V6.1: Draggable + Resizable Icons with react-rnd */}
        {styleOptions.showIcons !== false && activeIconEmojis.length > 0 && (
          <Rnd
            position={{ x: iconState.x, y: iconState.y }}
            size={{ width: iconState.width, height: iconState.height }}
            onDragStop={handleIconDragStop}
            onResizeStop={handleIconResizeStop}
            bounds="parent"
            minWidth={60}
            minHeight={24}
            maxWidth={200}
            maxHeight={60}
            className="z-20"
          >
            <div 
              className="w-full h-full flex items-center justify-center gap-1 cursor-move bg-white/20 backdrop-blur-sm px-2 py-1 rounded-lg hover:bg-white/30 transition-colors border border-white/30"
              title="Arrastra para mover, bordes para redimensionar"
            >
              {activeIconEmojis.map((emoji, i) => (
                <span key={i} className="drop-shadow-md" style={{ fontSize: Math.min(iconState.height * 0.7, 24) }}>{emoji}</span>
              ))}
            </div>
          </Rnd>
        )}
        
        {/* Title */}
        {title && (
          <div 
            className="absolute w-full px-4 text-center pointer-events-none"
            style={{ top: activeIconEmojis.length > 0 ? "12%" : "8%", color: textColor }}
          >
            <h3 
              className="font-extrabold drop-shadow-md line-clamp-2 leading-tight"
              style={{ fontSize: TEXT_SIZE_MAP.title[styleOptions.titleSize || "L"] }}
            >
              {title}
            </h3>
          </div>
        )}
        
        {/* Subtitle */}
        {subtitle && (
          <div 
            className="absolute w-full px-4 text-center pointer-events-none"
            style={{ top: "22%", color: secondaryColor }}
          >
            <p 
              className="font-medium"
              style={{ fontSize: TEXT_SIZE_MAP.subtitle[styleOptions.subtitleSize || "M"] }}
            >
              {subtitle}
            </p>
          </div>
        )}
        
        {/* Slogan */}
        {slogan && (
          <div 
            className="absolute w-full px-4 text-center pointer-events-none"
            style={{ top: "30%", color: textColor + "DD" }}
          >
            <p 
              className="font-semibold drop-shadow-sm line-clamp-2"
              style={{ fontSize: TEXT_SIZE_MAP.slogan[styleOptions.sloganSize || "M"] }}
            >
              {slogan}
            </p>
          </div>
        )}
        
        {/* V6.1: QR CARD - ALWAYS CENTERED, NO TEXT BELOW */}
        <div className="absolute left-1/2 -translate-x-1/2" style={{ top: "38%" }}>
          <div className="relative">
            {/* Shadow */}
            <div className="absolute inset-0 bg-black/15 blur-lg rounded-xl translate-y-1" />
            {/* QR Card */}
            <div className="relative bg-white rounded-xl p-2.5 shadow-lg border border-slate-100">
              {isLoadingQr ? (
                /* Loading state */
                <div className="w-[110px] h-[110px] bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex flex-col items-center justify-center">
                  <Loader2 className="w-6 h-6 text-emerald-500 animate-spin mb-1" />
                  <span className="text-[9px] text-slate-400">{sqT("sticker.loading_qr")}</span>
                </div>
              ) : qrInfo?.qr_locked ? (
                /* CORTE 4-C — gate de pago: QR placeholder marca de agua */
                <div className="w-[110px] h-[110px] bg-slate-100 rounded-lg relative overflow-hidden flex items-center justify-center" data-testid="sticker-qr-locked">
                  <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "repeating-linear-gradient(45deg, #64748b 0 6px, transparent 6px 12px)" }} />
                  <div className="relative flex flex-col items-center text-center px-1">
                    <span className="text-2xl">🔒</span>
                    <span className="text-[8px] font-bold text-slate-600 leading-tight mt-1">{sqT("sticker.locked_label")}</span>
                  </div>
                </div>
              ) : qrImageUrl ? (
                /* REAL QR Image */
                <img 
                  src={qrImageUrl}
                  alt="Código QR" 
                  className="w-[110px] h-[110px] object-contain"
                  onError={(e) => {
                    setTimeout(() => {
                      const baseUrl = qrInfo?.qr_url || "";
                      const sep = baseUrl.includes('?') ? '&' : '?';
                      e.target.src = `${baseUrl}${sep}retry=${Date.now()}`;
                    }, 1000);
                  }}
                />
              ) : (
                /* Fallback */
                <div 
                  className="w-[110px] h-[110px] bg-slate-100 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-slate-200 transition-colors"
                  onClick={onRefreshQr}
                >
                  <RefreshCw className="w-6 h-6 text-slate-400 mb-1" />
                  <span className="text-[9px] text-slate-400">{sqT("sticker.click_to_load")}</span>
                </div>
              )}
            </div>
          </div>
          {/* V6.1: CLEAN - NO "Escanéame si bloqueo" TEXT */}
          <p className="text-center text-[10px] mt-2 font-medium" style={{ color: textColor + "CC" }}>
            AvisoCar.com
          </p>
        </div>
        
        {/* V6.1: Draggable + Resizable Character/Emoji with react-rnd */}
        {styleOptions.characterEnabled && styleOptions.characterId && (
          <Rnd
            position={{ x: emojiState.x, y: emojiState.y }}
            size={{ width: emojiState.width, height: emojiState.height }}
            onDragStop={handleEmojiDragStop}
            onResizeStop={handleEmojiResizeStop}
            bounds="parent"
            minWidth={30}
            minHeight={30}
            maxWidth={100}
            maxHeight={100}
            className="z-20"
            lockAspectRatio={true}
          >
            <div 
              className="w-full h-full flex items-center justify-center cursor-move bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors border border-white/30"
              title="Arrastra para mover, bordes para redimensionar"
            >
              <AnimatedCharacter 
                emoji={STICKER_CHARACTERS?.find(c => c.id === styleOptions.characterId)?.emoji || "🚗"}
                animation={styleOptions.characterAnimation || "float"}
                size={Math.min(emojiState.width, emojiState.height) * 0.7}
              />
            </div>
          </Rnd>
        )}
        
        {/* V6.1: FOOTER SAFE AREA (12% reserved) - BIGGER BRANDING */}
        <div 
          className="absolute bottom-0 left-0 right-0 text-center bg-gradient-to-t from-black/20 to-transparent flex flex-col items-center justify-end"
          style={{ height: `${Math.round(previewSize * 0.12)}px`, paddingBottom: '8px' }}
        >
          <p className="text-base font-bold tracking-wide" style={{ color: textColor }}>
            🚗 {STICKER_FOOTER.line1}
          </p>
          <p className="text-xs leading-tight mt-0.5 opacity-90" style={{ color: textColor }}>
            {getStickerFooter(sqLang).line2}
          </p>
        </div>
      </motion.div>
      
      {/* Last update timestamp */}
      {qrInfo?.updated_at && (
        <p className="text-[9px] text-slate-400 mt-2 text-center">
          Actualizado: {new Date(qrInfo.updated_at).toLocaleString('es-PE', { 
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
          })}
        </p>
      )}
    </div>
  );
};

// ============================================
// V6.1: ICON SELECTOR - IMPROVED SELECTION + REMOVE ALL
// ============================================
const IconSelector = ({ activeIcons, onToggle, onRemoveAll, showIcons, onShowIconsChange, categoryId }) => {
  const { t } = useI18n();
  const icons = getIconsForCategory(categoryId);
  const selectedCount = activeIcons?.length || 0;
  
  return (
    <div className="space-y-3">
      {/* Show icons toggle with counter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-700 font-medium">{t("sticker_extra.icons_top")}</Label>
          {selectedCount > 0 && (
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0">
              {selectedCount}/3
            </Badge>
          )}
        </div>
        <Switch 
          checked={showIcons !== false}
          onCheckedChange={onShowIconsChange}
        />
      </div>
      
      {showIcons !== false && (
        <>
          {/* V6.1: Info about drag + resize */}
          <p className="text-[10px] text-purple-600 bg-purple-50 px-2 py-1 rounded">
            {t("sticker_extra.drag_icons_hint")}
          </p>
          
          {/* V6.1: Remove all button */}
          {selectedCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRemoveAll}
              className="w-full h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              {t("sticker_extra.remove_all", { n: selectedCount })}
            </Button>
          )}
          
          {/* Icon grid with better selection feedback */}
          <div className="grid grid-cols-5 gap-1.5">
            {icons.map((icon) => {
              const isActive = activeIcons.includes(icon.id);
              const canSelect = selectedCount < 3 || isActive;
              
              return (
                <motion.button
                  key={icon.id}
                  onClick={() => canSelect && onToggle(icon.id)}
                  whileHover={{ scale: canSelect ? 1.08 : 1 }}
                  whileTap={{ scale: canSelect ? 0.92 : 1 }}
                  disabled={!canSelect}
                  className={`
                    relative flex flex-col items-center gap-0.5 p-1.5 rounded-lg border-2 transition-all
                    ${isActive 
                      ? "border-purple-500 bg-purple-50 text-purple-700 shadow-md shadow-purple-200" 
                      : canSelect
                        ? "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                        : "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
                    }
                  `}
                  data-testid={`icon-${icon.id}`}
                >
                  <span className="text-lg">{icon.icon}</span>
                  <span className="text-[8px] leading-tight font-medium">{t(`sticker_icons.${icon.id}`) !== `sticker_icons.${icon.id}` ? t(`sticker_icons.${icon.id}`) : icon.name}</span>
                  {/* V6.1: X button to remove selected icon */}
                  {isActive && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-600 rounded-full flex items-center justify-center">
                      <X className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
          <p className="text-[10px] text-slate-400">
            {selectedCount === 3 ? t("sticker_extra.max_reached") : t("sticker_extra.select_up_to")}
          </p>
        </>
      )}
    </div>
  );
};

// ============================================
// V5 FIX: GRADIENT PICKER
// ============================================
const GradientPicker = ({ selected, onSelect }) => {
  const { t } = useI18n();
  return (
    <div className="space-y-2">
      <Label className="text-[11px] text-slate-500">{t("sticker_extra.background_gradient")}</Label>
      <div className="grid grid-cols-6 gap-1.5">
        {GRADIENT_PRESETS.map((preset) => (
          <motion.button
            key={preset.id}
            onClick={() => onSelect(preset)}
            whileHover={{ scale: 1.12 }}
            whileTap={{ scale: 0.95 }}
            className={`
              w-7 h-7 rounded-lg border-2 transition-all
              ${selected?.id === preset.id 
                ? "border-slate-800 ring-2 ring-emerald-500/40" 
                : "border-transparent hover:border-slate-300"
              }
            `}
            style={{ background: generateGradientCSS(preset) }}
            title={preset.name}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================
// V5 FIX: COLOR PICKER
// ============================================
const ColorPicker = ({ selected, onSelect, label }) => {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] text-slate-500">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {TEXT_COLORS.map((color) => (
          <motion.button
            key={color.id}
            onClick={() => onSelect(color)}
            whileHover={{ scale: 1.15 }}
            whileTap={{ scale: 0.9 }}
            className={`
              w-6 h-6 rounded-full border-2 transition-all
              ${selected?.id === color.id 
                ? "border-slate-800 ring-2 ring-emerald-500/40" 
                : "border-slate-200 hover:border-slate-300"
              }
            `}
            style={{ backgroundColor: color.value }}
            title={color.name}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================
// V6.0: CHARACTER SELECTOR - DRAG BASED (NO FIXED POSITIONS)
// ============================================
const CharacterSelector = ({ selected, onSelect, enabled, onToggle }) => {
  const { t } = useI18n();
  const characters = STICKER_CHARACTERS || [
    { id: "car_happy", emoji: "🚗", name: "Auto" },
    { id: "dog", emoji: "🐕", name: "Perrito" },
    { id: "cat", emoji: "🐱", name: "Gatito" },
    { id: "star", emoji: "⭐", name: "Estrella" },
    { id: "heart", emoji: "❤️", name: "Corazón" },
    { id: "thumbsup", emoji: "👍", name: "OK" },
  ];
  
  // V6.0: REMOVED fixed positions - now uses drag
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-slate-700 font-medium">{t("sticker_extra.decorative_sticker")}</Label>
        <Switch checked={enabled} onCheckedChange={onToggle} />
      </div>
      {enabled && (
        <>
          {/* V6.0: Drag info */}
          <p className="text-[10px] text-purple-600 bg-purple-50 px-2 py-1 rounded">
            💡 Arrastra el emoji en la vista previa para posicionarlo
          </p>
          
          <div className="grid grid-cols-6 gap-1.5">
            {characters.slice(0, 12).map((char) => (
              <motion.button
                key={char.id}
                onClick={() => onSelect(char.id)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`
                  p-1.5 rounded-lg border-2 transition-all
                  ${selected === char.id 
                    ? "border-purple-500 bg-purple-50 shadow-md shadow-purple-200" 
                    : "border-slate-200 bg-white hover:border-slate-300"
                  }
                `}
                title={char.name}
              >
                <AnimatedCharacter emoji={char.emoji} animation="float" size={18} />
              </motion.button>
            ))}
          </div>
          
          {/* V6.1: Remove emoji button */}
          {selected && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSelect(null)}
              className="w-full h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Quitar emoji
            </Button>
          )}
          
          {/* V6.1: Drag + resize info */}
          <p className="text-[10px] text-purple-600 bg-purple-50 px-2 py-1 rounded">
            {t("sticker_extra.drag_emoji_hint")}
          </p>
        </>
      )}
    </div>
  );
};

// ============================================
// V5.1: AI VARIANT STRIP COMPONENT
// ============================================
const AIVariantStrip = ({ variants, selectedIndex, onSelect, isVisible }) => {
  if (!isVisible || !variants || variants.length === 0) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-3"
    >
      <div className="flex items-center gap-2 mb-2">
        <Bot className="w-4 h-4 text-purple-500" />
        <span className="text-xs font-semibold text-slate-700">Variantes sugeridas por IA:</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {variants.map((variant, index) => {
          const isSelected = selectedIndex === index;
          const gradient = variant.designTokens.backgroundGradient;
          
          return (
            <motion.button
              key={variant.id}
              onClick={() => onSelect(index)}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              className={`
                relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all
                ${isSelected 
                  ? "border-purple-500 ring-2 ring-purple-500/40 shadow-lg" 
                  : "border-slate-200 hover:border-slate-300"
                }
              `}
              style={{ 
                background: gradient ? generateGradientCSS(gradient) : "#10B981"
              }}
              title={variant.name}
            >
              {/* Mini preview */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                <span className="text-[8px] font-bold opacity-90">{index + 1}</span>
                <span className="text-lg">{variant.designTokens.activeIcons?.[0] ? 
                  PREMIUM_ICONS.vehiculos.find(i => i.id === variant.designTokens.activeIcons[0])?.icon || "✨" 
                  : "✨"
                }</span>
              </div>
              
              {/* Selection indicator */}
              {isSelected && (
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center shadow"
                >
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                </motion.div>
              )}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
};

// ============================================
// V5.1: AI CONFIGURATION PANEL
// ============================================
const AIConfigPanel = ({ 
  isOpen, 
  onClose, 
  category, 
  onGenerate,
  isGenerating 
}) => {
  const { t } = useI18n();
  const [selectedTone, setSelectedTone] = useState("familiar");
  const [urgency, setUrgency] = useState(3);
  const [country] = useState("PE");
  
  const handleGenerate = () => {
    onGenerate({
      category,
      tone: selectedTone,
      country,
      urgency,
    });
  };
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200 space-y-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
            <Cpu className="w-4 h-4 text-white" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-800">{t("sticker_extra.ai_designer")}</h4>
            <p className="text-[10px] text-slate-500">Genera 5 variantes únicas</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Tone selector */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-600">{t("sticker_extra.emotional_tone")}</Label>
        <div className="grid grid-cols-2 gap-2">
          {AI_TONES.map((tone) => (
            <button
              key={tone.id}
              onClick={() => setSelectedTone(tone.id)}
              className={`
                flex items-center gap-2 p-2 rounded-lg border text-left transition-all
                ${selectedTone === tone.id 
                  ? "border-purple-500 bg-purple-50 text-purple-700" 
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }
              `}
            >
              <span className="text-lg">{tone.icon}</span>
              <div>
                <p className="text-xs font-medium">{tone.name}</p>
                <p className="text-[9px] text-slate-400">{tone.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Urgency slider */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-slate-600">{t("sticker_extra.urgency_level")}</Label>
          <Badge className={`text-[10px] ${
            urgency >= 4 ? "bg-red-100 text-red-700" :
            urgency >= 3 ? "bg-amber-100 text-amber-700" :
            "bg-green-100 text-green-700"
          }`}>
            {AI_URGENCY_LEVELS.find(l => l.id === urgency)?.name}
          </Badge>
        </div>
        <div className="flex gap-1">
          {AI_URGENCY_LEVELS.map((level) => (
            <button
              key={level.id}
              onClick={() => setUrgency(level.id)}
              className={`
                flex-1 h-2 rounded-full transition-all
                ${urgency >= level.id 
                  ? level.id >= 4 ? "bg-red-400" : level.id >= 3 ? "bg-amber-400" : "bg-green-400"
                  : "bg-slate-200"
                }
              `}
              title={level.description}
            />
          ))}
        </div>
      </div>
      
      {/* Generate button */}
      <Button
        onClick={handleGenerate}
        disabled={isGenerating}
        className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white font-medium"
      >
        {isGenerating ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("sticker_extra.generating_variants")}
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            {t("sticker_extra.generate_variants")}
          </>
        )}
      </Button>
      
      <p className="text-[9px] text-center text-slate-400">
        La IA genera tokens de diseño, no imágenes. Tu QR siempre está centrado y seguro.
      </p>
    </motion.div>
  );
};

// ============================================
// MAIN COMPONENT: StickerBuilderProV5 (V6.1 - STICKER REAL)
// ============================================
const StickerBuilderProV5 = ({ 
  isOpen, 
  onClose, 
  vehicleId, 
  vehicleName 
}) => {
  const { t, lang } = useI18n();
  const stickerCatalog = useStickerCatalog();
  // State
  const [activeTab, setActiveTab] = useState("templates");
  const [selectedCategory, setSelectedCategory] = useState("vehiculos");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [qrInfo, setQrInfo] = useState(null);
  const [isLoadingQr, setIsLoadingQr] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // V6.1: Zoom state (50-150%)
  const [zoomLevel, setZoomLevel] = useState(100);
  
  // V5.1: AI State
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiVariants, setAiVariants] = useState([]);
  const [selectedAIVariant, setSelectedAIVariant] = useState(-1);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  
  // V5 FIX: Style state with proper history for undo/redo
  const [styleState, setStyleState] = useState({ ...DEFAULT_STYLE_STATE });
  const historyRef = useRef(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  // Initialize history on mount
  useEffect(() => {
    if (!historyRef.current) {
      historyRef.current = new StyleHistory(DEFAULT_STYLE_STATE);
    }
  }, []);
  
  // Update history state indicators
  const updateHistoryState = useCallback(() => {
    if (historyRef.current) {
      setCanUndo(historyRef.current.canUndo());
      setCanRedo(historyRef.current.canRedo());
    }
  }, []);
  
  // V5 FIX: Update style with proper history tracking
  const updateStyleWithHistory = useCallback((updates) => {
    setStyleState(prev => {
      const newState = { ...prev, ...updates };
      
      // Push to history immediately
      if (historyRef.current) {
        historyRef.current.push(newState);
        setTimeout(updateHistoryState, 0);
      }
      
      setHasUnsavedChanges(true);
      return newState;
    });
  }, [updateHistoryState]);
  
  // V6.0: Undo handler (SILENT - no toast spam)
  const handleUndo = useCallback(() => {
    if (historyRef.current?.canUndo()) {
      const prevState = historyRef.current.undo();
      if (prevState) {
        setStyleState(prevState);
        updateHistoryState();
        // V6.0: SILENT undo - no toast
      }
    }
  }, [updateHistoryState]);
  
  // V6.0: Redo handler (SILENT - no toast spam)
  const handleRedo = useCallback(() => {
    if (historyRef.current?.canRedo()) {
      const nextState = historyRef.current.redo();
      if (nextState) {
        setStyleState(nextState);
        updateHistoryState();
        // V6.0: SILENT redo - no toast
      }
    }
  }, [updateHistoryState]);
  
  // V5 FIX: Keyboard shortcuts (Ctrl+Z, Ctrl+Shift+Z)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if ((e.ctrlKey || e.metaKey) && e.key === "z") {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      }
      
      // Ctrl+Y for redo (Windows convention)
      if ((e.ctrlKey || e.metaKey) && e.key === "y") {
        e.preventDefault();
        handleRedo();
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleUndo, handleRedo]);
  
  // V5.2: Load saved sticker config
  const loadSavedConfig = useCallback(async () => {
    if (!vehicleId) return;
    
    try {
      const token = localStorage.getItem("avisocar_token");
      const response = await fetch(`${API}/api/user/sticker-config/${vehicleId}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.found && data.config) {
          const cfg = data.config;
          // Apply saved config to state
          // 4-D2: textos guardados sin personalizar → idioma del dueño
          const locTexts = localizeSavedTexts(cfg.texts, cfg.template_id, lang);
          const savedState = {
            ...DEFAULT_STYLE_STATE,
            title: locTexts.title || DEFAULT_STYLE_STATE.title,
            subtitle: locTexts.subtitle || DEFAULT_STYLE_STATE.subtitle,
            slogan: locTexts.slogan || DEFAULT_STYLE_STATE.slogan,
            titleSize: cfg.text_sizes?.title || "L",
            subtitleSize: cfg.text_sizes?.subtitle || "M",
            sloganSize: cfg.text_sizes?.slogan || "M",
            activeIcons: cfg.icons?.active || [],
            iconPosition: cfg.icons?.position || "top-right",
            showIcons: cfg.icons?.show !== false,
            frameEnabled: cfg.effects?.frame_enabled !== false,
            glowEnabled: cfg.effects?.glow_enabled || false,
            characterEnabled: cfg.effects?.character_enabled || false,
            characterId: cfg.effects?.character_id || null,
            characterPosition: cfg.effects?.character_position || "bottom-left",
            // V6.3: Load layout offsets for drag/resize positions
            layoutOffsets: cfg.layout_offsets || cfg.layoutOffsets || {},
          };
          
          // Find and apply background gradient
          if (cfg.colors?.background_gradient) {
            // V6.3: Also check if gradient object itself is directly usable
            if (cfg.colors.background_gradient.colors) {
              savedState.backgroundGradient = cfg.colors.background_gradient;
            } else {
              const gradient = GRADIENT_PRESETS.find(g => g.id === cfg.colors.background_gradient.id);
              if (gradient) savedState.backgroundGradient = gradient;
            }
          }
          
          // Find and apply text colors
          if (cfg.colors?.primary_text) {
            const textColor = TEXT_COLORS.find(c => c.value === cfg.colors.primary_text.value);
            if (textColor) savedState.primaryTextColor = textColor;
          }
          
          setStyleState(savedState);
          if (historyRef.current) {
            historyRef.current.reset(savedState);
          }
          
          toast.success(t("sticker_extra.prev_loaded"), { duration: 2000 });
        }
      }
    } catch (error) {
      console.warn("Could not load saved config:", error);
    }
  }, [vehicleId, lang, t]);
  
  // Fetch QR data
  const loadQrData = useCallback(async () => {
    if (!vehicleId) return;
    
    setIsLoadingQr(true);
    try {
      const data = await fetchLatestQR(vehicleId);
      setQrInfo(data);
      toast.success(t("sticker_extra.qr_synced"), { duration: 1500 });
    } catch (error) {
      console.error("Error loading QR:", error);
      toast.error(t("sticker_extra.qr_load_error"));
      // Retry after 2 seconds
      setTimeout(() => loadQrData(), 2000);
    } finally {
      setIsLoadingQr(false);
    }
  }, [vehicleId, t]);
  
  // Load QR and saved config on open
  useEffect(() => {
    if (isOpen && vehicleId) {
      loadQrData();
      loadSavedConfig(); // V5.2: Load saved configuration
      
      // Reset state when opening (will be overwritten by savedConfig if exists)
      if (historyRef.current) {
        historyRef.current.reset(DEFAULT_STYLE_STATE);
      }
      setSelectedTemplate(null);
      setHasUnsavedChanges(false);
      updateHistoryState();
    }
  }, [isOpen, vehicleId, loadQrData, loadSavedConfig, updateHistoryState]);
  
  // When template is selected, apply its defaults
  useEffect(() => {
    if (selectedTemplate) {
      const gradient = GRADIENT_PRESETS.find(g => 
        g.id === selectedTemplate.palette || 
        g.name.toLowerCase() === selectedTemplate.palette?.toLowerCase()
      ) || GRADIENT_PRESETS[0];
      
      updateStyleWithHistory({
        title: selectedTemplate.title || "",
        subtitle: selectedTemplate.subline || "",
        slogan: selectedTemplate.slogan || "",
        backgroundGradient: gradient,
        customEmoji: selectedTemplate.emoji || "",
      });
    }
  }, [selectedTemplate, updateStyleWithHistory]);
  
  // V5 FIX: Handle "Cambiar diseño" with unsaved changes check
  const handleChangeDesign = useCallback(() => {
    if (hasUnsavedChanges) {
      // Show confirmation
      const confirmed = window.confirm("Tienes cambios sin guardar. ¿Deseas descartarlos?");
      if (!confirmed) return;
    }
    
    setSelectedTemplate(null);
    setActiveTab("templates");
    setHasUnsavedChanges(false);
    // Clear AI variants when changing design
    setAiVariants([]);
    setSelectedAIVariant(-1);
  }, [hasUnsavedChanges]);
  
  // V5.1: Generate AI Variants
  const handleGenerateAIVariants = useCallback(async (params) => {
    setIsGeneratingAI(true);
    
    try {
      // Simulate slight delay for UX
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const variants = generateAIVariants(params);
      setAiVariants(variants);
      setSelectedAIVariant(-1);
      setShowAIPanel(false);
      
      toast.success(
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          <span>¡5 variantes generadas!</span>
        </div>,
        { duration: 3000 }
      );
    } catch (error) {
      console.error("AI generation error:", error);
      toast.error(t("sticker_extra.variants_error"));
    } finally {
      setIsGeneratingAI(false);
    }
  }, [t]);
  
  // V5.1: Apply AI Variant
  const handleSelectAIVariant = useCallback((index) => {
    if (index < 0 || index >= aiVariants.length) return;
    
    const variant = aiVariants[index];
    setSelectedAIVariant(index);
    
    // Apply design tokens to style state
    const tokens = variant.designTokens;
    updateStyleWithHistory({
      backgroundGradient: tokens.backgroundGradient,
      primaryTextColor: tokens.primaryTextColor,
      secondaryTextColor: tokens.secondaryTextColor,
      title: tokens.title,
      subtitle: tokens.subtitle,
      slogan: tokens.slogan,
      activeIcons: tokens.activeIcons || [],
      iconPosition: tokens.iconPosition,
      showIcons: tokens.showIcons,
      characterId: tokens.characterId,
      characterEnabled: tokens.characterEnabled,
      characterPosition: tokens.characterPosition,
      frameEnabled: tokens.frameEnabled,
      glowEnabled: tokens.glowEnabled,
    });
    
    toast.success(`Variante ${index + 1} aplicada`, { duration: 1500 });
  }, [aiVariants, updateStyleWithHistory]);
  
  // V6.2: Autosave indicator state
  const [autosaveStatus, setAutosaveStatus] = useState("idle"); // idle, saving, saved
  const autosaveTimeoutRef = useRef(null);
  
  // V6.2: Autosave function with debounce
  const triggerAutosave = useCallback(async (configPatch) => {
    if (!vehicleId) return;
    
    // Cancel pending autosave
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    // Debounce: wait 800ms before saving
    autosaveTimeoutRef.current = setTimeout(async () => {
      setAutosaveStatus("saving");
      
      try {
        const token = localStorage.getItem("avisocar_token");
        const response = await fetch(`${API}/api/user/sticker-config/autosave`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            vehicle_id: vehicleId,
            patch: configPatch
          })
        });
        
        if (response.ok) {
          setAutosaveStatus("saved");
          setHasUnsavedChanges(false);
          // Reset to idle after 2 seconds
          setTimeout(() => setAutosaveStatus("idle"), 2000);
        }
      } catch (error) {
        console.error("Autosave error:", error);
        setAutosaveStatus("idle");
      }
    }, 800);
  }, [vehicleId]);
  
  // V6.2: Trigger autosave when style changes
  useEffect(() => {
    if (hasUnsavedChanges && vehicleId) {
      const configPatch = {
        template_id: selectedTemplate?.id || "custom",
        texts: {
          title: styleState.title,
          subtitle: styleState.subtitle,
          slogan: styleState.slogan
        },
        colors: {
          background_gradient: styleState.backgroundGradient,
          primary_text: styleState.primaryTextColor,
          secondary_text: styleState.secondaryTextColor
        },
        icons: {
          active: styleState.activeIcons,
          show: styleState.showIcons
        },
        effects: {
          frame_enabled: styleState.frameEnabled,
          glow_enabled: styleState.glowEnabled,
          character_enabled: styleState.characterEnabled,
          character_id: styleState.characterId
        },
        layout_offsets: styleState.layoutOffsets || {}
      };
      triggerAutosave(configPatch);
    }
  }, [styleState, hasUnsavedChanges, triggerAutosave, selectedTemplate, vehicleId]);
  
  // V6.2: Save sticker configuration (manual save)
  const handleSaveConfig = async () => {
    if (!vehicleId) {
      toast.error(t("sticker_extra.vehicle_id_error"));
      return;
    }
    
    setIsSaving(true);
    try {
      const token = localStorage.getItem("avisocar_token");
      const response = await fetch(`${API}/api/user/sticker-config/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          vehicle_id: vehicleId,
          template_id: selectedTemplate?.id || "custom",
          text_sizes: {
            title: styleState.titleSize || "L",
            subtitle: styleState.subtitleSize || "M",
            slogan: styleState.sloganSize || "M"
          },
          texts: {
            title: styleState.title,
            subtitle: styleState.subtitle,
            slogan: styleState.slogan
          },
          colors: {
            background_gradient: styleState.backgroundGradient,
            primary_text: styleState.primaryTextColor,
            secondary_text: styleState.secondaryTextColor
          },
          icons: {
            active: styleState.activeIcons,
            position: styleState.iconPosition,
            show: styleState.showIcons
          },
          effects: {
            frame_enabled: styleState.frameEnabled,
            glow_enabled: styleState.glowEnabled,
            character_enabled: styleState.characterEnabled,
            character_id: styleState.characterId,
            character_position: styleState.characterPosition
          },
          // V6.2: Include layout offsets for drag/resize positions
          layout_offsets: styleState.layoutOffsets || {}
        })
      });
      
      if (response.ok) {
        setHasUnsavedChanges(false);
        setAutosaveStatus("saved");
        toast.success("✅ Sticker guardado correctamente", { duration: 3000 });
      } else {
        const error = await response.json();
        throw new Error(error.detail || "Error al guardar");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error(t("sticker_extra.save_error") + ": " + error.message);
    } finally {
      setIsSaving(false);
    }
  };
  
  // V6.2: Download handler - ENSURES CURRENT STATE IS USED
  const handleDownload = async (format) => {
    // CORTE 4-C — gate de pago: sin membresía activa no hay descarga
    if (qrInfo?.qr_locked) {
      toast.error(t("sticker.locked_toast"));
      return;
    }
    if (!styleState.title) {
      toast.error(t("sticker.title_required"));
      return;
    }
    
    // Verify QR is loaded
    if (!qrInfo?.qr_url && !qrInfo?.qr_token) {
      toast.error(t("sticker.loading_wait"));
      await loadQrData();
      return;
    }
    
    setIsDownloading(true);
    setDownloadFormat(format);
    
    try {
      // V6.2: CRITICAL - Flush autosave first to ensure server has latest
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
      
      // Wait for 2 animation frames to ensure UI is synced
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      
      // Refresh QR one more time before download with cache bust
      const freshQr = await fetchLatestQR(vehicleId);
      setQrInfo(freshQr);
      
      // V6.2: Create snapshot of CURRENT STATE
      const exportConfig = {
        title: styleState.title,
        subtitle: styleState.subtitle,
        slogan: styleState.slogan,
        titleSize: styleState.titleSize,
        subtitleSize: styleState.subtitleSize,
        sloganSize: styleState.sloganSize,
        backgroundGradient: styleState.backgroundGradient,
        primaryTextColor: styleState.primaryTextColor,
        secondaryTextColor: styleState.secondaryTextColor,
        activeIcons: styleState.activeIcons,
        showIcons: styleState.showIcons,
        iconPosition: styleState.iconPosition,
        characterEnabled: styleState.characterEnabled,
        characterId: styleState.characterId,
        frameEnabled: styleState.frameEnabled,
        glowEnabled: styleState.glowEnabled,
        layoutOffsets: styleState.layoutOffsets
      };
      
      // V6.5: Debug logging to verify export data
      console.log("V6.5 Export Config:", {
        characterId: exportConfig.characterId,
        characterEnabled: exportConfig.characterEnabled,
        activeIcons: exportConfig.activeIcons,
        layoutOffsets: exportConfig.layoutOffsets
      });
      
      // V6.2: IMPORTANT - Pass texts DIRECTLY as customTitle/customSlogan/customSubtitle
      // These take priority over template values in generateSticker
      const result = await generateSticker({
        vehicleId,
        // V6.2: Use minimal template - let exportConfig control everything
        template: { 
          id: "custom_export", 
          palette: styleState.backgroundGradient?.palette || selectedTemplate?.palette || "emerald_forest",
          layout: selectedTemplate?.layout || "hero"
        },
        // V6.2: CRITICAL - Pass current texts directly (these override template)
        customTitle: styleState.title,
        customSubtitle: styleState.subtitle,
        customSlogan: styleState.slogan,
        // Full style options
        styleOptions: exportConfig,
        format
      });
      
      if (result.success) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        
        toast.success(
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-500" />
            <span>¡Sticker {format.toUpperCase()} descargado!</span>
          </div>,
          { duration: 4000 }
        );
        
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error(t("sticker_extra.generate_error") + ": " + error.message);
    } finally {
      setIsDownloading(false);
      setDownloadFormat(null);
    }
  };
  
  // Icon toggle handler
  const handleIconToggle = (iconId) => {
    const newIcons = styleState.activeIcons.includes(iconId)
      ? styleState.activeIcons.filter(id => id !== iconId)
      : [...styleState.activeIcons, iconId].slice(0, 3); // Max 3 icons
    updateStyleWithHistory({ activeIcons: newIcons });
  };
  
  // V6.1: Remove all icons handler
  const handleRemoveAllIcons = () => {
    updateStyleWithHistory({ activeIcons: [] });
  };
  
  // V6.1: Handle element resize
  const handleElementResize = (element, newState) => {
    updateStyleWithHistory({
      layoutOffsets: {
        ...styleState.layoutOffsets,
        [element]: newState
      }
    });
  };
  
  // Get templates for current category — fuente canónica: API del backend.
  const categoryTemplates = stickerCatalog
    .templatesByCategory(selectedCategory)
    .map((tp) => localizeTemplate(tp, lang));
  
  return (
    <>
      <Confetti show={showConfetti} />
      
      <Dialog open={isOpen} onOpenChange={(open) => {
        if (!open && hasUnsavedChanges) {
          const confirmed = window.confirm(t("sticker_extra.unsaved_confirm"));
          if (!confirmed) return;
        }
        onClose();
      }}>
        <DialogContent 
          className="max-w-7xl w-[98vw] p-0 bg-slate-50 overflow-hidden flex flex-col"
          style={{ height: "min(94vh, 900px)" }}
          data-testid="sticker-builder-modal"
        >
          {/* Accessibility */}
          <DialogTitle className="sr-only">Sticker Builder Pro V6.1</DialogTitle>
          
          {/* V6.1: Sticky Header with version badge */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b bg-white shrink-0">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  Sticker Builder Pro
                  <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[9px] font-semibold px-2">V6.1</Badge>
                </h2>
                <p className="text-[11px] text-slate-500">{vehicleName || "Mi vehículo"}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1.5">
              {/* V6.2: Autosave Status Indicator */}
              {autosaveStatus === "saving" && (
                <span className="text-[10px] text-slate-400 flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Guardando...
                </span>
              )}
              {autosaveStatus === "saved" && (
                <span className="text-[10px] text-emerald-600 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  {t("sticker_extra.saved")}
                </span>
              )}
              
              {/* V6.2: Manual Save Button (now secondary since autosave is primary) */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveConfig}
                disabled={isSaving || autosaveStatus === "saved"}
                className="h-8 px-3 border-emerald-500 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1.5" />
                )}
                {t("sticker.save")}
              </Button>
              
              <div className="h-5 w-px bg-slate-200 mx-1" />
              
              {/* V5.1: AI Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAIPanel(!showAIPanel)}
                className={`h-8 px-3 ${showAIPanel ? "bg-purple-50 border-purple-300 text-purple-700" : ""}`}
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                {t("sticker.generate_ai")}
              </Button>
              
              <div className="h-5 w-px bg-slate-200 mx-1" />
              
              {/* V6.0: Undo/Redo buttons (SILENT) */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleUndo}
                disabled={!canUndo}
                className="h-8 px-2 text-slate-500 hover:text-slate-700 disabled:opacity-30"
                title="Deshacer (Ctrl+Z)"
              >
                <Undo2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRedo}
                disabled={!canRedo}
                className="h-8 px-2 text-slate-500 hover:text-slate-700 disabled:opacity-30"
                title="Rehacer (Ctrl+Shift+Z)"
              >
                <Redo2 className="w-4 h-4" />
              </Button>
              
              <div className="h-5 w-px bg-slate-200 mx-1" />
              
              {/* Close button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          {/* V6.1: Main Content - Wider layout for bigger preview */}
          <div className="flex-1 flex overflow-hidden">
            
            {/* LEFT PANEL: Preview with zoom (wider) */}
            <div className="w-[480px] shrink-0 border-r bg-white flex flex-col">
              {/* V5.1: AI Variant Strip */}
              {aiVariants.length > 0 && (
                <div className="p-3 border-b bg-gradient-to-r from-purple-50 to-indigo-50">
                  <AIVariantStrip
                    variants={aiVariants}
                    selectedIndex={selectedAIVariant}
                    onSelect={handleSelectAIVariant}
                    isVisible={true}
                  />
                </div>
              )}
              
              {/* V6.1: Zoom Controls */}
              <div className="px-4 py-2 border-b bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ZoomOut className="w-4 h-4 text-slate-400" />
                  <input
                    type="range"
                    min="50"
                    max="150"
                    value={zoomLevel}
                    onChange={(e) => setZoomLevel(parseInt(e.target.value))}
                    className="w-24 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                  />
                  <ZoomIn className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-500 ml-1">{zoomLevel}%</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setZoomLevel(100)}
                  className="text-xs text-slate-500 h-6 px-2"
                >
                  Reset
                </Button>
              </div>
              
              {/* Preview area - centered, scrollable for large zoom */}
              <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
                <LivePreview
                  template={selectedTemplate}
                  styleOptions={styleState}
                  qrInfo={qrInfo}
                  isLoadingQr={isLoadingQr}
                  onRefreshQr={loadQrData}
                  zoomLevel={zoomLevel}
                  onDragStop={(element, pos) => {
                    // V6.1: Save drag positions
                    updateStyleWithHistory({
                      layoutOffsets: {
                        ...styleState.layoutOffsets,
                        [element]: pos
                      }
                    });
                  }}
                  onResizeStop={(element, state) => {
                    // V6.1: Save resize state
                    handleElementResize(element, state);
                  }}
                />
              </div>
              
              {/* Download buttons - sticky bottom */}
              <div className="p-3 border-t bg-slate-50 space-y-2 shrink-0">
                {qrInfo?.qr_locked && (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-center" data-testid="sticker-locked-banner">
                    <p className="text-[11px] font-semibold text-amber-800">{t("sticker.locked_label")}</p>
                    <Button
                      size="sm"
                      className="mt-1.5 h-8 w-full bg-amber-500 hover:bg-amber-600 text-white font-bold"
                      onClick={() => { window.location.href = `/checkout/${vehicleId}`; }}
                      data-testid="sticker-locked-pay-cta"
                    >
                      {t("sticker.locked_cta")}
                    </Button>
                  </div>
                )}
                <Button
                  onClick={() => handleDownload("png")}
                  disabled={isDownloading || qrInfo?.qr_locked}
                  className="w-full h-9 bg-emerald-500 hover:bg-emerald-600 text-white font-medium disabled:opacity-50"
                  data-testid="sticker-download-png-btn"
                >
                  {isDownloading && downloadFormat === "png" ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Image className="w-4 h-4 mr-2" />
                  )}
                  {t("sticker.download_png")}
                </Button>
                <Button
                  onClick={() => handleDownload("pdf")}
                  disabled={isDownloading || qrInfo?.qr_locked}
                  variant="outline"
                  className="w-full h-9 disabled:opacity-50"
                  data-testid="sticker-download-pdf-btn"
                >
                  {isDownloading && downloadFormat === "pdf" ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2" />
                  )}
                  {t("sticker.download_pdf")}
                </Button>
                
                {/* V5 FIX: "Cambiar diseño" ALWAYS VISIBLE */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleChangeDesign}
                  className="w-full text-slate-500 hover:text-emerald-600"
                >
                  <Layers className="w-4 h-4 mr-2" />
                  {t("sticker.change_design")}
                </Button>
              </div>
            </div>
            
            {/* CENTER + RIGHT: Tabs Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
                {/* Tab Navigation */}
                <div className="px-4 pt-3 pb-2 bg-white border-b shrink-0">
                  <TabsList className="grid w-full max-w-md grid-cols-2 bg-slate-100">
                    <TabsTrigger value="templates" className="data-[state=active]:bg-white text-sm">
                      <Grid3X3 className="w-4 h-4 mr-2" />
                      {t("sticker.templates_tab")}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="editor" 
                      className="data-[state=active]:bg-white text-sm"
                      disabled={!selectedTemplate && !styleState.title}
                    >
                      <Edit3 className="w-4 h-4 mr-2" />
                      {t("sticker.edit_tab")}
                    </TabsTrigger>
                  </TabsList>
                </div>
                
                {/* V5 FIX: Tab Content with PROPER SCROLL */}
                <TabsContent 
                  value="templates" 
                  className="flex-1 m-0 p-0 overflow-hidden"
                  style={{ maxHeight: "calc(100% - 60px)" }}
                >
                  <div className="h-full overflow-y-auto p-4">
                    {/* Category selector */}
                    <div className="mb-4">
                      <CategorySelector 
                        selectedCategory={selectedCategory}
                        onSelect={setSelectedCategory}
                        categories={stickerCatalog.categories}
                      />
                    </div>
                    
                    {/* V5 FIX: Template Grid with PROPER ALIGNMENT */}
                    <div 
                      className="grid gap-3"
                      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}
                    >
                      {categoryTemplates.map((template) => (
                        <TemplateCard
                          key={template.id}
                          template={template}
                          isSelected={selectedTemplate?.id === template.id}
                          onClick={() => {
                            setSelectedTemplate(template);
                            // Auto-switch to editor tab when template selected
                            setTimeout(() => setActiveTab("editor"), 300);
                          }}
                        />
                      ))}
                    </div>
                    
                    {categoryTemplates.length === 0 && (
                      <div className="text-center py-12 text-slate-400">
                        <LayoutTemplate className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">{t("sticker_extra.no_templates")}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                {/* V5 FIX: Editor Tab */}
                <TabsContent 
                  value="editor" 
                  className="flex-1 m-0 p-0 overflow-hidden"
                  style={{ maxHeight: "calc(100% - 60px)" }}
                >
                  <div className="h-full overflow-y-auto p-4 space-y-5">
                    {/* V5.1: AI Config Panel */}
                    <AnimatePresence>
                      {showAIPanel && (
                        <AIConfigPanel
                          isOpen={showAIPanel}
                          onClose={() => setShowAIPanel(false)}
                          category={selectedCategory}
                          onGenerate={handleGenerateAIVariants}
                          isGenerating={isGeneratingAI}
                        />
                      )}
                    </AnimatePresence>
                    
                    {/* Text Editor Section */}
                    <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-3">
                      <div className="flex items-center gap-2 text-slate-700 mb-2">
                        <Type className="w-4 h-4" />
                        <h4 className="text-sm font-semibold">{t("sticker_extra.editable_texts")}</h4>
                      </div>
                      
                      <div className="space-y-3">
                        {/* V5.2: Title with size control */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-[11px] text-slate-500">{t("sticker_extra.title_max")}</Label>
                            <div className="flex gap-1">
                              {["S", "M", "L", "XL"].map((size) => (
                                <button
                                  key={size}
                                  onClick={() => updateStyleWithHistory({ titleSize: size })}
                                  className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                                    (styleState.titleSize || "L") === size 
                                      ? "bg-emerald-500 text-white" 
                                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                  }`}
                                >
                                  {size}
                                </button>
                              ))}
                            </div>
                          </div>
                          <Input
                            value={styleState.title}
                            onChange={(e) => updateStyleWithHistory({ title: e.target.value.slice(0, 28) })}
                            placeholder={t("sticker_extra.placeholder_title")}
                            className="bg-white border-slate-200 h-9"
                            maxLength={28}
                          />
                        </div>
                        
                        {/* V5.2: Subtitle with size control */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-[11px] text-slate-500">{t("sticker_extra.subtitle_max")}</Label>
                            <div className="flex gap-1">
                              {["S", "M", "L"].map((size) => (
                                <button
                                  key={size}
                                  onClick={() => updateStyleWithHistory({ subtitleSize: size })}
                                  className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                                    (styleState.subtitleSize || "M") === size 
                                      ? "bg-emerald-500 text-white" 
                                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                  }`}
                                >
                                  {size}
                                </button>
                              ))}
                            </div>
                          </div>
                          <Input
                            value={styleState.subtitle}
                            onChange={(e) => updateStyleWithHistory({ subtitle: e.target.value.slice(0, 40) })}
                            placeholder="Contacto privado con AvisoCar"
                            className="bg-white border-slate-200 h-9"
                            maxLength={40}
                          />
                        </div>
                        
                        {/* V5.2: Slogan with size control */}
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-[11px] text-slate-500">{t("sticker_extra.slogan_max")}</Label>
                            <div className="flex gap-1">
                              {["S", "M", "L"].map((size) => (
                                <button
                                  key={size}
                                  onClick={() => updateStyleWithHistory({ sloganSize: size })}
                                  className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                                    (styleState.sloganSize || "M") === size 
                                      ? "bg-emerald-500 text-white" 
                                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                  }`}
                                >
                                  {size}
                                </button>
                              ))}
                            </div>
                          </div>
                          <Input
                            value={styleState.slogan}
                            onChange={(e) => updateStyleWithHistory({ slogan: e.target.value.slice(0, 45) })}
                            placeholder={t("sticker_extra.placeholder_slogan")}
                            className="bg-white border-slate-200 h-9"
                            maxLength={45}
                          />
                          <p className="text-[9px] text-slate-400 mt-1 text-right">{styleState.slogan?.length || 0}/45</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Colors Section */}
                    <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-3">
                      <div className="flex items-center gap-2 text-slate-700 mb-2">
                        <Palette className="w-4 h-4" />
                        <h4 className="text-sm font-semibold">{t("sticker_extra.colors")}</h4>
                      </div>
                      
                      <GradientPicker
                        selected={styleState.backgroundGradient}
                        onSelect={(g) => updateStyleWithHistory({ backgroundGradient: g })}
                      />
                      
                      <div className="grid grid-cols-2 gap-4">
                        <ColorPicker
                          label={t("sticker_extra.primary_text")}
                          selected={styleState.primaryTextColor}
                          onSelect={(c) => updateStyleWithHistory({ primaryTextColor: c })}
                        />
                        
                        <ColorPicker
                          label={t("sticker_extra.secondary_text")}
                          selected={styleState.secondaryTextColor}
                          onSelect={(c) => updateStyleWithHistory({ secondaryTextColor: c })}
                        />
                      </div>
                    </div>
                    
                    {/* Icons Section */}
                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                      <div className="flex items-center gap-2 text-slate-700 mb-3">
                        <Smile className="w-4 h-4" />
                        <h4 className="text-sm font-semibold">{t("sticker_extra.icons_top")}</h4>
                      </div>
                      
                      <IconSelector
                        activeIcons={styleState.activeIcons || []}
                        onToggle={handleIconToggle}
                        onRemoveAll={handleRemoveAllIcons}
                        showIcons={styleState.showIcons}
                        onShowIconsChange={(v) => updateStyleWithHistory({ showIcons: v })}
                        categoryId={selectedCategory}
                      />
                    </div>
                    
                    {/* Character Section - V6.1: With remove button */}
                    <div className="bg-white rounded-xl p-4 border border-slate-200">
                      <CharacterSelector
                        selected={styleState.characterId}
                        onSelect={(id) => updateStyleWithHistory({ characterId: id, characterEnabled: id ? true : false })}
                        enabled={styleState.characterEnabled || false}
                        onToggle={(v) => updateStyleWithHistory({ characterEnabled: v })}
                      />
                    </div>
                    
                    {/* Effects Section */}
                    <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-3">
                      <div className="flex items-center gap-2 text-slate-700 mb-2">
                        <Zap className="w-4 h-4" />
                        <h4 className="text-sm font-semibold">{t("sticker_extra.effects")}</h4>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                          <span className="text-xs text-slate-600">{t("sticker_extra.visible_frame")}</span>
                          <Switch
                            checked={styleState.frameEnabled !== false}
                            onCheckedChange={(v) => updateStyleWithHistory({ frameEnabled: v })}
                          />
                        </div>
                        <div className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                          <span className="text-xs text-slate-600">{t("sticker_extra.glow_effect")}</span>
                          <Switch
                            checked={styleState.glowEnabled || false}
                            onCheckedChange={(v) => updateStyleWithHistory({ glowEnabled: v })}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Footer Info (read-only) */}
                    <div className="bg-slate-100 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Eye className="w-4 h-4 text-slate-500" />
                        <h4 className="text-xs font-semibold text-slate-600">{t("sticker_extra.footer_fixed")}</h4>
                      </div>
                      <p className="text-[10px] text-slate-500 mb-2">
                        {t("sticker_extra.footer_fixed_desc")}
                      </p>
                      <div className="p-2.5 bg-white rounded-lg border border-slate-200">
                        <p className="text-xs font-bold text-slate-700">🚗 {STICKER_FOOTER.line1}</p>
                        <p className="text-[9px] text-slate-500 mt-0.5">{getStickerFooter(lang).line2}</p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StickerBuilderProV5;
